import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Offcanvas, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  FiEye, FiEyeOff, FiUser, FiList, FiShoppingBag, FiLogOut,
  FiChevronRight, FiShield, FiMapPin, FiCheckCircle, FiBell
} from 'react-icons/fi';
import NotificationModal from './NotificationModal';
import CreateAccountModal from './CreateAccountModal';
import EditAccountModal from './EditAccountModal';
import { STOREFRONT_BRAND } from '../utils/brandAssets';
import styles from './bodywon.module.css';
import { apiFetch, setAuthToken, setAdminAuthToken, getAuthToken } from '../admin/jscontrol/apiClient';
import StorageManager from '../admin/jscontrol/StorageManager';
import {
  isAdminUser,
  resolveRoleNameFromLogin,
  normalizeLoginPayload,
  getProfileDisplayName,
  resolveUserIdFromLogin,
  parseUserIdFromToken,
  BLOCKED_USER_MESSAGE,
  isUserBlockedFromLogin,
  isUserBlockedById,
  isBlockedUserErrorMessage,
  consumeAdminReturnUrl,
  clearAdminReturnUrl,
} from '../utils/authHelpers';
import { fetchCurrentUserProfile } from '../utils/profileApi';

const LoginOffcanvas = ({ show, onHide, onLoginSuccess, onLogoutSuccess }) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationCount, setNotificationCount] = useState(3);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loggedInName, setLoggedInName] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);

  useEffect(() => {
    if (!show) return;
    const token = getAuthToken();
    if (!token) return;

    if (!StorageManager.userId()) {
      const id = parseUserIdFromToken(token);
      if (id) StorageManager.userId(id);
    }

    let cancelled = false;

    (async () => {
      try {
        const profile = await fetchCurrentUserProfile();
        const username = profile?.form?.username?.trim();
        if (!cancelled && username) {
          setIsLoggedIn(true);
          setLoggedInName(username);
          StorageManager.username(username);
          return;
        }
      } catch {
        /* fall back to stored username */
      }

      const storedUsername = StorageManager.username()?.trim();
      if (!cancelled && storedUsername) {
        setIsLoggedIn(true);
        setLoggedInName(storedUsername);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!email.trim() || !password.trim()) {
      setLoginError('Please enter username and password.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiFetch('/Auth/Login', {
        method: 'POST',
        data: { Username: email, Password: password },
      });
      const loginUsername = email.trim();
      const data = response.data;
      const payload = normalizeLoginPayload(data, loginUsername);

      const isSuccess =
        payload?.isSuccess ?? payload?.IsSuccess ??
        payload?.success ?? payload?.Success ??
        data?.isSuccess ?? data?.IsSuccess ??
        data?.success ?? data?.Success;

      const token =
        payload?.token || payload?.Token || payload?.accessToken ||
        data?.token || data?.Token || data?.accessToken;

      if (!isSuccess && !token) {
        const failMessage =
          payload?.message || payload?.Message || data?.message || data?.Message ||
          'Invalid Username or Password.';
        setLoginError(
          isBlockedUserErrorMessage(failMessage) ? BLOCKED_USER_MESSAGE : failMessage
        );
        return;
      }

      if (isUserBlockedFromLogin(data, loginUsername)) {
        setLoginError(BLOCKED_USER_MESSAGE);
        return;
      }

      const authToken = token || 'authenticated';
      const userId = resolveUserIdFromLogin(data, loginUsername);
      if (await isUserBlockedById(authToken, userId)) {
        setLoginError(BLOCKED_USER_MESSAGE);
        return;
      }

      const displayName = getProfileDisplayName(data, loginUsername);
      const roleName = resolveRoleNameFromLogin(data, loginUsername);
      const goToAdmin = isAdminUser(data, loginUsername);

      if (userId) StorageManager.userId(userId);
      StorageManager.username(displayName);
      StorageManager.roleName(roleName);

      if (goToAdmin) {
        setAdminAuthToken(authToken);
        toast.success('Login successful. Opening admin panel…');
        onHide();
        navigate(consumeAdminReturnUrl());
        return;
      }

      clearAdminReturnUrl();
      setAuthToken(authToken);

      setLoggedInName(displayName);
      setIsLoggedIn(true);
      onLoginSuccess?.();
      toast.success('Login Successful!');
    } catch (err) {
      const message = err.message || 'Server error. Please try again.';
      setLoginError(isBlockedUserErrorMessage(message) ? BLOCKED_USER_MESSAGE : message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setEmail('');
    setPassword('');
    setLoggedInName('');
    setLoginError('');
    setNotificationCount(3);
    setAuthToken(null);
    StorageManager.clear();
    onLogoutSuccess?.();
    toast.info('You have been logged out.');
  };

  const handleNotificationClick = () => {
    setShowNotificationModal(true);
    setNotificationCount(0);
  };

  const handleCartClick = (e) => {
    e.preventDefault();
    onHide();
    navigate('/cart');
  };

  const handleAddressClick = (e) => {
    e.preventDefault();
    onHide();
    navigate('/account/addresses');
  };

  const handleOrderHistoryClick = (e) => {
    e.preventDefault();
    onHide();
    navigate('/account/orders');
  };

  const handleEditProfileClick = () => {
    setShowEditAccount(true);
  };

  const handleProfileUpdated = (username) => {
    const name = String(username || '').trim();
    if (!name) return;
    setLoggedInName(name);
    StorageManager.username(name);
  };

  const LoginView = (
    <div className={styles.loginContainer}>
      <div className={styles.loginLogo}>
        <img src={STOREFRONT_BRAND.logo} alt="BodyWon" width={200} height={50} style={{ objectFit: 'contain' }} />
      </div>
      <h2 className={styles.loginTitle}>Login</h2>
      <Form onSubmit={handleLogin}>
        <Form.Group className="mb-4" controlId="formBasicEmail">
          <Form.Control
            type="text"
            placeholder="Username"
            className={styles.loginInput}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            required
          />
        </Form.Group>
        <Form.Group className="mb-2" controlId="formBasicPassword">
          <div className={styles.passwordWrapper}>
            <Form.Control
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className={styles.loginInput}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
            />
            <span onClick={() => setShowPassword(!showPassword)} className={styles.passwordToggle}>
              {showPassword ? <FiEye /> : <FiEyeOff />}
            </span>
          </div>
        </Form.Group>
        <div className={styles.loginLinksRow}>
          <a
            href="#create-account"
            className={styles.createAccountLink}
            onClick={(e) => {
              e.preventDefault();
              setShowCreateAccount(true);
            }}
          >
            Create Account
          </a>
          <a href="#forgot-password" className={styles.forgotPassword} onClick={(e) => e.preventDefault()}>
            Forgot Your Password?
          </a>
        </div>
        {loginError && (
          <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '10px', textAlign: 'center' }}>
            {loginError}
          </div>
        )}
        <Button variant="primary" type="submit" className={styles.loginButton} disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </Form>
    </div>
  );

  const ProfileView = (
    <div className={styles.profileContainer}>
      <div className={styles.profileGreetingRow}>
        <div className={styles.profileAvatar}>
          <FiUser size={24} />
        </div>
        <a
          href="/account/profile"
          className={styles.profileNameLink}
          onClick={(e) => {
            e.preventDefault();
            handleEditProfileClick();
          }}
        >
          Hello, {loggedInName || 'User'}
        </a>
        <div className={styles.profileGreetingSpacer} aria-hidden="true" />
        <div className={styles.notificationIconWrapper} onClick={handleNotificationClick}>
          <FiBell className={styles.notificationIcon} />
          {notificationCount > 0 && (
            <span className={styles.notificationBadge}>
              {notificationCount}
            </span>
          )}
        </div>
      </div>

      <div className={styles.profileSection}>
        <h5 className={styles.sectionTitle}>Orders</h5>
        <div className={styles.profileCard}>
          <a href="/account/orders" className={styles.profileLinkItem} onClick={handleOrderHistoryClick}>
            <FiList size={22} className={styles.profileLinkIcon} />
            <span>Order History</span>
            <FiChevronRight className={styles.arrowIcon} />
          </a>
          <a href="/cart" className={styles.profileLinkItem} onClick={handleCartClick}>
            <FiShoppingBag size={22} className={styles.profileLinkIcon} />
            <span>Cart</span>
            <FiChevronRight className={styles.arrowIcon} />
          </a>
        </div>
      </div>

      <div className={styles.profileSection}>
        <h5 className={styles.sectionTitle}>Account Setting</h5>
        <div className={styles.profileCard}>
          <a href="#" className={styles.profileSettingItem}>
            <FiShield size={22} className={styles.profileLinkIcon} />
            <span>Login &amp; Security</span>
          </a>
          <a href="/account/addresses" className={styles.profileSettingItem} onClick={handleAddressClick}>
            <FiMapPin size={22} className={styles.profileLinkIcon} />
            <span>Your Address</span>
          </a>
          <a href="#" className={styles.profileSettingItem}>
            <FiCheckCircle size={22} className={styles.profileLinkIcon} />
            <span>Review Your Purchase</span>
          </a>
        </div>
      </div>

      <Button variant="danger" onClick={handleLogout} className={styles.logoutButton}>
        <FiLogOut className={styles.logoutIcon} />
        <span>Logout</span>
      </Button>
    </div>
  );

  return (
    <>
      <Offcanvas show={show} onHide={onHide} placement="end" className={styles.loginOffcanvas}>
        <Offcanvas.Header closeButton>
          {isLoggedIn && (
            <Offcanvas.Title className={styles.offcanvasTitle}>
              Profile
            </Offcanvas.Title>
          )}
        </Offcanvas.Header>
        <Offcanvas.Body>
          {isLoggedIn ? ProfileView : LoginView}
        </Offcanvas.Body>
      </Offcanvas>

      <NotificationModal
        show={showNotificationModal}
        onHide={() => setShowNotificationModal(false)}
      />

      <CreateAccountModal
        show={showCreateAccount}
        onHide={() => setShowCreateAccount(false)}
        onCreated={(username) => setEmail(username)}
      />

      <EditAccountModal
        show={showEditAccount}
        onHide={() => setShowEditAccount(false)}
        onUpdated={handleProfileUpdated}
      />
    </>
  );
};

export default LoginOffcanvas;
